import React from 'react'
import PropTypes from 'prop-types'
import SimpleReactCalendar from 'simple-react-calendar'
import Icon from 'components/Icon'
import classnames from 'classnames'
import startOfDay from 'date-fns/start_of_day'
import endOfDay from 'date-fns/end_of_day'
import subMonths from 'date-fns/sub_months'
import addMonths from 'date-fns/add_months'
import isWithinRange from 'date-fns/is_within_range'
import isBefore from 'date-fns/is_before'
import max from 'date-fns/max'
import {getDynamicDateRange} from 'redux/modules/currentUser/utils'
import partial from 'utils/partial'
import propsChanged from 'utils/propsChanged'
import moment from 'moment'

export const rangePresets = {
  today: 'Today',
  yesterday: 'Yesterday',
  thisWeek: 'This week',
  lastWeek: 'Last week',
  thisMonth: 'This month',
  last28: 'Last 28 days',
  lastMonth: 'Last month',
  thisYear: 'This year',
  lastYear: 'Last year'
}

export default class RangeCalendar extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    today: PropTypes.instanceOf(Date),
    selected: PropTypes.object,
    onSelect: PropTypes.func,
    onApply: PropTypes.func,
    dynamicDateType: PropTypes.string,
    isSelectInProgress: PropTypes.bool
  }

  constructor(props) {
    super(props)
    this.state = {
      selectedMonth: null,
      isInverted: false,
      wasSelecting: false,
      isInitialDate: true
    }
  }

  componentWillMount() {
    const {minDate, today} = this.props
    this.setState({
      selectedMonth: minDate || today
    })
  }

  componentWillReceiveProps(nextProps) {
    if (propsChanged(this.props, nextProps, ['isSelectInProgress'])) {
      this.setState({
        wasSelecting: nextProps.isSelectInProgress,
        isInitialDate: false
      })
    } else {
      this.setState({
        isInitialDate: false
      })
    }
  }

  render() {
    const {className} = this.props
    const classNames = classnames('calendar-range', className)

    return (
      <div className={classNames}>
        {this.renderCalendars()}
        {this.renderPresets()}
      </div>
    )
  }

  renderCalendars() {
    const {
      minDate,
      maxDate,
      selected: {start, end}
    } = this.props
    const {selectedMonth, isInitialDate} = this.state
    const calendarProps = {
      maxDate,
      minDate,
      highlighted: (!isInitialDate && start && end) ? {start, end} : null,
      onSelect: this.onDatepickerSelect,
      onDayHover: this.onDayHover
    }

    return (
      <div className='calendar-range_calendars'>
        <div className='calendar-range_start'>
          <SimpleReactCalendar
            {...calendarProps}
            headerPrevArrow={<Icon className='calendar_icon-left' name='chevron' size='10px' />}
            activeMonth={subMonths(selectedMonth, 1)}
            onMonthChange={this.onSelectPrevMonth}
          />
        </div>
        <div className='calendar-range_end'>
          <SimpleReactCalendar
            {...calendarProps}
            headerNextArrow={<Icon className='calendar_icon-right' name='chevron' size='10px' />}
            activeMonth={selectedMonth}
            onMonthChange={this.onSelectNextMonth}
          />
        </div>
      </div>
    )
  }

  renderPresets() {
    const presets = Object.keys(rangePresets).map(presetId => {
      return [rangePresets[presetId], partial(this.onSelectPreset, presetId)]
    })
    const {dynamicDateType} = this.props
    return (
      <div className='calendar-range_presets'>
        {
          presets.map((preset, index) => {
            const [label, func] = preset
            const classActive = rangePresets[dynamicDateType] === label ? ' actived' : ''
            return (
              <div
                key={index}
                className={'calendar-range_preset' + classActive}
                onClick={partial(func, true)}
                onMouseEnter={partial(func, false)}
              >
                {label}
              </div>
            )
          })
        }
      </div>
    )
  }

  onSelectPrevMonth = () => {
    const {selectedMonth} = this.state
    this.setState({
      selectedMonth: subMonths(selectedMonth, 1)
    })
  }

  onSelectNextMonth = () => {
    const {selectedMonth} = this.state
    this.setState({
      selectedMonth: addMonths(selectedMonth, 1)
    })
  }

  onSelectPreset = (dynamicDateType, forceApply) => {
    const localTimeZone = moment.tz.guess()
    const {today} = this.props
    const selectedRange = getDynamicDateRange(dynamicDateType, today, null, null, localTimeZone)

    this.onDateSelect({
      selectedRange,
      forceApply,
      dynamicDateType
    })
  }

  onDatepickerSelect = value => {
    const {selected: {start, end}, isSelectInProgress} = this.props

    if (!isSelectInProgress) {
      return this.onDateSelect({
        selectedRange: {
          start: value,
          end: value
        },
        isSelectInProgress: !isSelectInProgress
      })
    }

    return this.onDateSelect({
      selectedRange: {
        start: start,
        end: end
      },
      isSelectInProgress: !isSelectInProgress
    })
  }

  onDayHover = value => {
    const {selected: {start, end}, isSelectInProgress} = this.props
    const {isInverted} = this.state
    if (!isSelectInProgress) return null

    const isDateBefore = isBefore(value, start)
    const nextInverted = isBefore(value, end)

    if (isInverted && nextInverted) {
      this.setState({
        isInverted: nextInverted
      })
      this.onDateSelect({
        selectedRange: {
          start: value,
          end
        },
        isSelectInProgress
      })
      return
    }

    if (isInverted && !nextInverted) {
      this.setState({
        isInverted: nextInverted
      })
      return this.onDateSelect({
        selectedRange: {
          start: end,
          end: value
        },
        isSelectInProgress
      })
    }

    this.setState({
      isInverted: isDateBefore
    })

    return this.onDateSelect({
      selectedRange: {
        start: isDateBefore ? value : start,
        end: isDateBefore ? start : value
      },
      isSelectInProgress
    })
  }

  onDateSelect({selectedRange, isSelectInProgress = false, forceApply = false, dynamicDateType = false}) {
    const {onSelect, onApply, minDate, maxDate} = this.props
    const {wasSelecting} = this.state
    const {start: selectedStart, end: selectedEnd} = selectedRange
    const start = max(minDate, selectedStart)
    const end = isWithinRange(selectedEnd, start, maxDate) ? selectedEnd : maxDate

    if (forceApply || (wasSelecting && !isSelectInProgress)) {
      onApply({
        start: startOfDay(start),
        end: endOfDay(end),
        dynamicDateType
      })
    } else {
      onSelect({
        start: startOfDay(start),
        end: endOfDay(end)
      }, isSelectInProgress)
    }
  }
}



// WEBPACK FOOTER //
// ./client/components/Calendar/RangeCalendar.js